import type { VatPresentation } from '$lib/money';
import type { TotalPriceQuote } from '$lib/webparking/orders/total-price';
import type { ApplicableDiscount } from '$models/ApplicableDiscount/ApplicableDiscount';
import type { NonSubscribableProduct } from '$models/NonSubscribableProduct/NonSubscribableProduct';
import type { OrderPrices } from '$models/Order/OrderPrices';
import type {
	OrderConfigurationOrder,
	OrderConfiguratorConfiguration,
	OrderConfiguratorConfigurationCategory,
	OrderConfiguratorOptions
} from '$models/OrderConfigurator/OrderConfigurator';
import type { Product } from '$models/Product/Product';
import type { ProductCategory, ProductCategoryIcon } from '$models/Product/ProductCategory';
import { catalogPriceForDisplay, moneyForDisplay } from '$lib/money';
import { isRegularProduct } from '$lib/type-helpers';
import { getOrderPricesDiscountData, getOrderPricesProductsPrice } from '$lib/webparking';
import {
	clearTotalPriceCache,
	getTotalPrice,
	totalPriceRequest
} from '$lib/webparking/orders/total-price';
import { getContext, setContext } from 'svelte';

export type ConfiguratorState = ReturnType<typeof createConfiguratorState>;
export type ConfiguratorPricingSnapshot = {
	pricingSource?: 'order-price' | 'total-price';
	prices: OrderPrices;
	discountData: { onetime: ApplicableDiscount | null; recurring: ApplicableDiscount | null };
};
type CouponValidationState = 'none' | 'pending' | 'valid' | 'invalid';
type PricingRequestResult = ConfiguratorPricingSnapshot & {
	cacheKey: string;
};
type WebparkingPricingOrder = Parameters<typeof getOrderPricesProductsPrice>[0];

const key = Symbol('orderConfiguratorState');
const pricesCache = new Map<string, ConfiguratorPricingSnapshot>();
const pricesInFlightCache = new Map<string, Promise<PricingRequestResult>>();
const shopPricingKey = Symbol('shopPricing');
let pricingGeneration = $state(0);

export function getPricingGeneration() {
	return pricingGeneration;
}

export function setShopPricing() {
	setContext(shopPricingKey, true);
}

export function getShopPricing(): boolean {
	return getContext(shopPricingKey) === true;
}

// Pricing/discount data (e.g. coupon applicability) can differ per auth identity.
// Call this on every login/logout transition so a snapshot fetched under one
// identity is never served back after the identity changes.
export function clearConfiguratorPricingCache() {
	pricesCache.clear();
	pricesInFlightCache.clear();
	clearTotalPriceCache();
	pricingGeneration += 1;
}

function createEmptyPrices(): OrderPrices {
	return {
		productsLowestRecurringPrice: null,
		productsRecurringPrice: null,
		productsOneTimePrice: null,
		nonSubscribableProductPrices: {},
		totalNonSubscribableProductsPrice: 0,
		discountAmount: 0,
		discountAmountOnetime: 0,
		freeNonSubscribableProducts: []
	};
}

function clonePricingSnapshot(snapshot: ConfiguratorPricingSnapshot): ConfiguratorPricingSnapshot {
	return structuredClone(snapshot);
}

function asWebparkingPricingOrder(order: OrderConfigurationOrder): WebparkingPricingOrder {
	return order as unknown as WebparkingPricingOrder;
}

// Hardcoded values for the "proefpakket" eception
// @TODO: Remove this when backend implements a proper way to disallow specific quantities.
const HARDCODED_CATEGORY_IDS = [
	// Staging & production cups category ID's
	'9bec12b9-8752-4574-a9f0-d23cacd31ad6', // NL, staging & prod
	'9bec16cd-d60d-471f-9fcc-760019be6022', // DE, prod
	'9bd8369f-3bd3-4da3-a3f2-e411e60822b7' // DE, staging
];
const HARDCODED_COUPON_CODES = ['proefpakket10', 'probierpaket10'];

export function createConfiguratorState(
	configuration: OrderConfiguratorConfiguration,
	options?: OrderConfiguratorOptions
) {
	const usesTotalPrice = options?.pricingSource === 'total-price' && !options.subscriptionId;
	let defaultConfigurationCategory = $derived.by(() => {
		// If only one category is available, select it by default
		if (configuration.categories.length === 1) {
			return configuration.categories[0];
		}

		// If more categories exist and a default is defined, set it
		if (configuration.categories.length > 1 && configuration.defaultCategory) {
			return configuration.categories.find(({ productCategory }) => {
				return productCategory.id === configuration?.defaultCategory?.id;
			});
		}
	});
	let configurationCategory: OrderConfiguratorConfigurationCategory | undefined = $state(
		defaultConfigurationCategory
	);
	let discountData: { onetime: ApplicableDiscount | null; recurring: ApplicableDiscount | null } =
		$state({ onetime: null, recurring: null });
	let fromDiscountData: {
		onetime: ApplicableDiscount | null;
		recurring: ApplicableDiscount | null;
	} = $state({ onetime: null, recurring: null });
	let products = $derived(configurationCategory?.products ?? []);
	let nonSubscribableProducts = $state(configuration.nonSubscribableProducts ?? []);
	let selectedCategory: ProductCategory | undefined = $derived(
		configurationCategory?.productCategory
	);
	// Frequencies is set in the configuration and does not change between categories.
	let frequencies = configuration.frequencies;
	let minimumFrequency = $derived.by(() => {
		const recurring = frequencies.filter((frequency) => frequency > 0);
		return usesTotalPrice && recurring.length
			? Math.min(...recurring)
			: recurring.reduce((a, b) => Math.min(a, b), 1);
	});
	let selectedFrequency: number = $state(configuration.defaultFrequency); // Can be any frequency
	let selectedRecurringFrequency: number = $state(
		configuration.defaultFrequency > 0 ? configuration.defaultFrequency : minimumFrequency
	); // Always > 0
	let recurringCouponCode: string = $state('');
	let onetimeCouponCode: string = $state('');
	let couponCode = $derived(selectedFrequency > 0 ? recurringCouponCode : onetimeCouponCode);
	let quantities = $derived.by(() => {
		let quantities = configurationCategory?.quantities ?? [];

		// Hardcoded "proefpakket" exception
		// @TODO: Remove this when backend implements a proper way to disallow specific quantities.
		if (
			HARDCODED_CATEGORY_IDS.includes(configurationCategory?.productCategory.id || '') &&
			!HARDCODED_COUPON_CODES.includes(couponCode)
		) {
			// Remove 3 (30) from the valid quantities of the "cups" categories
			return quantities.filter((q) => q !== 3);
		}

		return quantities;
	});
	let minimumQuantity = $derived(quantities.length > 0 ? Math.min(...quantities) : 0);
	let maximumQuantity = $derived(quantities.reduce((a, b) => Math.max(a, b), 0));
	let maximumFrequency = $derived(frequencies.reduce((a, b) => Math.max(a, b), 0));
	let quantityMultiplier: number = $derived(products?.[0]?.product?.quantityMultiplier ?? 1);
	// Selected quantity is the total quantity of all products
	let selectedQuantity: number = $derived(products.reduce((acc, p) => acc + p.quantity, 0));
	let selectedNonSubscribableProductsQuantity: number = $derived(
		nonSubscribableProducts.reduce((acc, p) => acc + p.quantity, 0)
	);
	// Temporary hardcoded fix to disallow ordering 3 (30) cups (category id's)
	// @TODO: Refactor this when backend implements a proper way to disallow specific quantities.
	let isProefpakket: boolean = $derived.by(() => {
		if (
			selectedQuantity === 3 &&
			HARDCODED_CATEGORY_IDS.includes(selectedCategory?.id || '') &&
			HARDCODED_COUPON_CODES.includes(couponCode)
		) {
			return true;
		}

		return false;
	});
	let isValidCategory: boolean = $derived(!!selectedCategory);
	let isValidQuantity: boolean = $derived(quantities.includes(selectedQuantity));
	let isValidFrequency: boolean = $derived(frequencies.includes(selectedFrequency));
	let fromPriceQuantity: number | null = $derived.by(() => {
		if (isValidQuantity) {
			return null;
		}

		const defaultQuantity = configurationCategory?.defaultQuantity ?? 0;

		if (defaultQuantity > 0 && quantities.includes(defaultQuantity)) {
			return defaultQuantity;
		}

		return minimumQuantity > 0 ? minimumQuantity : null;
	});

	let couponData = $derived.by(() => {
		if (usesTotalPrice) return quoteDiscount(currentQuote());
		const activeDiscountData = isValidQuantity ? discountData : fromDiscountData;

		if (selectedFrequency === 0 && activeDiscountData.onetime) {
			return activeDiscountData.onetime;
		}

		if (selectedFrequency > 0 && activeDiscountData.recurring) {
			return activeDiscountData.recurring;
		}
	});
	let isValidCoupon: boolean = $derived.by(() => {
		if (!couponData) {
			return false;
		}

		return couponData.isApplicable;
	});
	let couponErrorMessage: string = $derived.by(() => {
		if (!couponData) {
			return '';
		}

		return couponData.noApplicableDiscountReason.message || '';
	});
	let couponValidationState: CouponValidationState = $derived.by(() => {
		if (!couponCode) {
			return 'none';
		}

		if (isFetchingPrices || !couponData) {
			return 'pending';
		}

		return couponData.isApplicable ? 'valid' : 'invalid';
	});
	let isFetchingPrices: boolean = $state(false);
	let latestPricesCacheKey: string | null = null;
	let latestFromPricesCacheKey: string | null = null;

	let productsTotal: number = $derived.by(() => {
		if (!prices) {
			return 0;
		}

		if (selectedFrequency > 0 && prices.productsRecurringPrice) {
			return prices.productsRecurringPrice;
		} else if (selectedFrequency === 0 && prices.productsOneTimePrice) {
			return prices.productsOneTimePrice;
		}

		return 0;
	});
	let productsBaseTotal: number = $derived(
		products.reduce((acc, p) => acc + p.product.basePrice * p.quantity, 0)
	);
	let nonSubscribableProductsTotal: number = $derived(
		nonSubscribableProducts.reduce((acc, p) => acc + p.product.price * p.quantity, 0)
	);

	let order: OrderConfigurationOrder = $derived.by(() => {
		return {
			frequency: selectedFrequency,
			products: products
				.filter((p) => p.quantity > 0)
				.map((p) => {
					return {
						id: p.product.id,
						quantity: p.quantity
					};
				}),
			nonSubscribableProducts: nonSubscribableProducts
				.filter((p) => p.quantity > 0)
				.map((p) => {
					return {
						id: p.product.id,
						quantity: p.quantity
					};
				}),
			couponCode: couponCode || null
		};
	});

	const defaultOptions: Required<OrderConfiguratorOptions> = {
		recurringCtaTooltip: {
			show: true,
			text: ''
		},
		pricingActivated: true,
		pricingSource: 'order-price',
		subscriptionId: ''
	};

	const parsedOptions: Required<OrderConfiguratorOptions> = options
		? { ...defaultOptions, ...options }
		: defaultOptions;

	// Subscription quotes change when the subscription changes, so they are only cached per configurator.
	const configuratorPricesCache = parsedOptions.subscriptionId
		? new Map<string, ConfiguratorPricingSnapshot>()
		: pricesCache;
	const configuratorPricesInFlightCache = parsedOptions.subscriptionId
		? new Map<string, Promise<PricingRequestResult>>()
		: pricesInFlightCache;

	let isPricingActivated: boolean = $state(parsedOptions.pricingActivated);
	let prices: OrderPrices = $state(createEmptyPrices());
	let fromPrices: OrderPrices = $state(createEmptyPrices());
	let committedPricesCacheKey: string | null = $state(null);
	let committedFromPricesCacheKey: string | null = $state(null);
	let quotes = $state.raw<Record<number, TotalPriceQuote>>({});
	let committedQuotesKey: string | null = $state(null);
	let quoteError = $state(false);
	let pendingQuotesKey: string | null = null;
	let pendingQuotes: Promise<void> | undefined;
	let quoteRevision = 0;
	let disposed = false;

	// This effect owns disposal only; request invalidation happens through the input key.
	$effect(() => () => {
		disposed = true;
		quoteRevision += 1;
	});

	function quoteOrders() {
		const reference = products.length > 0 && !isValidQuantity;
		const pricingOrder =
			reference && fromPriceQuantity !== null ? createPricingOrder(fromPriceQuantity) : order;
		// The CMS omits frequencies for non-subscribable-only shop items.
		const isOneTimeExtrasOnlyOrder =
			frequencies.length === 0 &&
			selectedFrequency === 0 &&
			products.length === 0 &&
			selectedNonSubscribableProductsQuantity > 0;
		if (
			!pricingOrder ||
			(!isValidFrequency && !isOneTimeExtrasOnlyOrder) ||
			(!pricingOrder.products.length && !pricingOrder.nonSubscribableProducts.length)
		)
			return [];
		const requestedFrequencies = new Set([selectedFrequency]);
		if (frequencies.some((frequency) => frequency > 0)) {
			requestedFrequencies.add(selectedRecurringFrequency);
			requestedFrequencies.add(minimumFrequency);
		}
		if (frequencies.includes(0)) requestedFrequencies.add(0);
		return [...requestedFrequencies]
			.sort((a, b) => a - b)
			.map((frequency) => ({
				...pricingOrder,
				frequency,
				products: pricingOrder.products.map((item) => ({ ...item })),
				nonSubscribableProducts: pricingOrder.nonSubscribableProducts.map((item) => ({ ...item })),
				couponCode: (frequency === 0 ? onetimeCouponCode : recurringCouponCode) || null
			}));
	}

	function totalQuotesKey(orders = quoteOrders()) {
		return JSON.stringify([
			pricingGeneration,
			products.length > 0 && !isValidQuantity,
			orders.map(totalPriceRequest)
		]);
	}

	function currentQuote(
		frequency = selectedFrequency,
		reference = products.length > 0 && !isValidQuantity
	) {
		if (!usesTotalPrice || reference !== (products.length > 0 && !isValidQuantity)) return null;
		try {
			return committedQuotesKey === totalQuotesKey() ? (quotes[frequency] ?? null) : null;
		} catch {
			return null;
		}
	}

	function quoteDiscount(quote: TotalPriceQuote | null): ApplicableDiscount | null {
		if (!quote) return null;
		const applied = quote.status === 'applied';
		return {
			isApplicable: applied,
			amount: applied ? moneyForDisplay(quote.discount) : 0,
			freeNonSubscribableProducts: applied
				? quote.gifts.map((gift) => ({
						id: gift.id,
						name: gift.name,
						quantity: gift.quantity,
						price: moneyForDisplay(gift.value),
						image: null
					}))
				: [],
			noApplicableDiscountReason: {
				code: quote.status === 'frequency-mismatch' ? 'frequency-mismatch' : 'unknown',
				message: quote.rejection?.message ?? ''
			},
			blockingPeriod: quote.blockingPeriod === null ? null : String(quote.blockingPeriod),
			requiredOrderCount: quote.requiredOrderCount,
			maximumOrderFrequency: quote.maximumOrderFrequency
		};
	}

	async function refreshTotalPrices() {
		let orders: OrderConfigurationOrder[];
		let requestKey: string;
		try {
			orders = quoteOrders();
			requestKey = totalQuotesKey(orders);
		} catch {
			quoteRevision += 1;
			pendingQuotesKey = null;
			isFetchingPrices = false;
			committedQuotesKey = null;
			quoteError = true;
			return;
		}
		if (pendingQuotesKey === requestKey) return pendingQuotes;
		const revision = ++quoteRevision;
		const generation = pricingGeneration;
		pendingQuotesKey = requestKey;
		isFetchingPrices = orders.length > 0;
		quoteError = false;
		committedQuotesKey = null;
		pendingQuotes = (async () => {
			try {
				const results = await Promise.all(
					orders.map(async (order) => [order.frequency, await getTotalPrice(order)] as const)
				);
				if (disposed || revision !== quoteRevision || generation !== pricingGeneration) return;
				quotes = Object.fromEntries(results);
				committedQuotesKey = requestKey;
				const data = {
					recurring: quoteDiscount(quotes[selectedRecurringFrequency] ?? null),
					onetime: quoteDiscount(quotes[0] ?? null)
				};
				if (products.length > 0 && !isValidQuantity) {
					fromDiscountData = data;
					discountData = { recurring: null, onetime: null };
				} else {
					discountData = data;
					fromDiscountData = { recurring: null, onetime: null };
				}
			} catch {
				if (!disposed && revision === quoteRevision && generation === pricingGeneration) {
					quoteError = true;
					resetDiscountData();
				}
			} finally {
				if (revision === quoteRevision) {
					isFetchingPrices = false;
					pendingQuotesKey = null;
				}
			}
		})();
		return pendingQuotes;
	}

	/** Display totals are baskets on the new path; legacy OrderPrices retain their meaning. */
	function displayPrices(reference = false, vat: VatPresentation = 'included') {
		const legacy = reference ? fromPrices : prices;
		const read = (frequency: number, before: number | null, discount: number) => {
			if (!usesTotalPrice)
				return { before, discount, payable: before === null ? null : before - discount };
			const quote = currentQuote(frequency, reference);
			if (!quote?.payable) return { before: null, discount: 0, payable: null };
			return {
				before: moneyForDisplay(quote.beforeDiscount, vat),
				discount: quote.status === 'applied' ? moneyForDisplay(quote.discount, vat) : 0,
				payable: moneyForDisplay(quote.payable, vat)
			};
		};
		const recurring = read(
			selectedRecurringFrequency,
			legacy.productsRecurringPrice,
			legacy.discountAmount
		);
		const onetime = read(0, legacy.productsOneTimePrice, legacy.discountAmountOnetime);
		const lowest = read(
			minimumFrequency,
			legacy.productsLowestRecurringPrice,
			legacy.discountAmount
		);
		return {
			recurring: recurring.before,
			onetime: onetime.before,
			lowestRecurring: lowest.before,
			recurringPayable: recurring.payable,
			onetimePayable: onetime.payable,
			lowestRecurringPayable: lowest.payable,
			recurringDiscount: recurring.discount,
			onetimeDiscount: onetime.discount,
			lowestRecurringDiscount: lowest.discount,
			extras: usesTotalPrice
				? nonSubscribableProducts.reduce<number | null>((total, { product, quantity }) => {
						if (!quantity) return total;
						const price = catalogPriceForDisplay(product.price, product.priceExcludingVat, vat);
						return total === null || price === null ? null : total + price * quantity;
					}, 0)
				: legacy.totalNonSubscribableProductsPrice
		};
	}

	function findProductItem(product: Product | NonSubscribableProduct) {
		const productList = isRegularProduct(product) ? products : nonSubscribableProducts;
		return productList.find((p) => p.product.id === product.id);
	}

	function resetDiscountData() {
		discountData.onetime = null;
		discountData.recurring = null;
		fromDiscountData.onetime = null;
		fromDiscountData.recurring = null;
	}

	function resetPrices() {
		prices = createEmptyPrices();
		committedPricesCacheKey = null;
	}

	function resetFromPrices() {
		fromPrices = createEmptyPrices();
		committedFromPricesCacheKey = null;
	}

	function resetAllPricing() {
		latestPricesCacheKey = null;
		latestFromPricesCacheKey = null;
		resetPrices();
		resetFromPrices();
		resetDiscountData();
	}

	function clearCouponPricing(type: 'recurring' | 'onetime') {
		if (type === 'recurring') {
			discountData.recurring = null;
			fromDiscountData.recurring = null;

			if (selectedFrequency > 0) {
				prices.discountAmount = 0;
				fromPrices.discountAmount = 0;
				prices.freeNonSubscribableProducts = [];
				fromPrices.freeNonSubscribableProducts = [];
			}

			return;
		}

		discountData.onetime = null;
		fromDiscountData.onetime = null;

		if (selectedFrequency === 0) {
			prices.discountAmountOnetime = 0;
			fromPrices.discountAmountOnetime = 0;
			prices.freeNonSubscribableProducts = [];
			fromPrices.freeNonSubscribableProducts = [];
		}
	}

	function setCouponCodes({
		recurringCode,
		onetimeCode
	}: {
		recurringCode: string;
		onetimeCode: string;
	}) {
		const recurringChanged = recurringCouponCode !== recurringCode;
		const onetimeChanged = onetimeCouponCode !== onetimeCode;

		if (!recurringChanged && !onetimeChanged) {
			return;
		}

		recurringCouponCode = recurringCode;
		onetimeCouponCode = onetimeCode;

		if (recurringChanged) {
			clearCouponPricing('recurring');
		}

		if (onetimeChanged) {
			clearCouponPricing('onetime');
		}

		if (isPricingActivated) {
			void refreshPrices();
		}
	}

	function getPricingCacheKey(categoryId: string, quantity: number) {
		const cacheKey = `${categoryId}:${selectedRecurringFrequency}:${quantity}:${onetimeCouponCode || 'x'}:${recurringCouponCode || 'x'}`;

		if (!parsedOptions.subscriptionId) {
			// Discounts can depend on the actual mix and extras, even at the same tier.
			return `${cacheKey}:${JSON.stringify([order.products, order.nonSubscribableProducts])}`;
		}

		return `${cacheKey}:${JSON.stringify(order.products)}`;
	}

	function createPricingOrder(quantity: number): OrderConfigurationOrder | null {
		const firstProduct = products[0];

		if (!firstProduct) {
			return null;
		}

		return {
			frequency: selectedFrequency,
			products: [
				{
					id: firstProduct.product.id,
					quantity
				}
			],
			nonSubscribableProducts: order.nonSubscribableProducts,
			couponCode: couponCode || null
		};
	}

	async function fetchPricingForOrder(
		pricingOrder: OrderConfigurationOrder,
		categoryId: string,
		quantity: number
	): Promise<PricingRequestResult> {
		const cacheKey = getPricingCacheKey(categoryId, quantity);
		const recurringFrequency = selectedRecurringFrequency;
		const recurringCode = recurringCouponCode;
		const onetimeCode = onetimeCouponCode;
		const extrasTotal = nonSubscribableProductsTotal;
		const cachedPrices = configuratorPricesCache.get(cacheKey);

		if (cachedPrices) {
			return {
				cacheKey,
				...clonePricingSnapshot(cachedPrices)
			};
		}

		const existingRequest = configuratorPricesInFlightCache.get(cacheKey);

		if (existingRequest) {
			const result = await existingRequest;
			return {
				cacheKey,
				...clonePricingSnapshot(result)
			};
		}

		const request = (async (): Promise<PricingRequestResult> => {
			isFetchingPrices = true;

			// This basically does the same as getOrderPrices but with more control.
			// @TODO: Deprecate getOrderPrices and maybe figure out a better way to do this
			const nextPrices: OrderPrices = {
				productsLowestRecurringPrice: await getOrderPricesProductsPrice(
					asWebparkingPricingOrder(pricingOrder),
					minimumFrequency,
					parsedOptions.subscriptionId
				),
				productsRecurringPrice: await getOrderPricesProductsPrice(
					asWebparkingPricingOrder(pricingOrder),
					recurringFrequency,
					parsedOptions.subscriptionId
				),
				productsOneTimePrice: await getOrderPricesProductsPrice(
					asWebparkingPricingOrder(pricingOrder),
					0
				),
				nonSubscribableProductPrices: {},
				totalNonSubscribableProductsPrice: 0,
				discountAmount: 0,
				discountAmountOnetime: 0,
				freeNonSubscribableProducts: []
			};

			const nextDiscountData: ConfiguratorPricingSnapshot['discountData'] = {
				onetime: null,
				recurring: null
			};

			// Catalogue automatic discounts must agree with the shop's total-price quote.
			// Protected account flows keep their existing explicit-coupon behavior.
			if (recurringCode || !parsedOptions.subscriptionId) {
				nextDiscountData.recurring = await getOrderPricesDiscountData(
					asWebparkingPricingOrder({
						...pricingOrder,
						frequency: recurringFrequency,
						couponCode: recurringCode
					}),
					{ ...nextPrices, totalNonSubscribableProductsPrice: extrasTotal }
				);

				if (nextDiscountData.recurring.isApplicable) {
					nextPrices.discountAmount = nextDiscountData.recurring.amount;
					nextPrices.freeNonSubscribableProducts =
						nextDiscountData.recurring.freeNonSubscribableProducts;
				}
			}

			if (onetimeCode || !parsedOptions.subscriptionId) {
				nextDiscountData.onetime = await getOrderPricesDiscountData(
					asWebparkingPricingOrder({
						...pricingOrder,
						frequency: 0,
						couponCode: onetimeCode
					}),
					{ ...nextPrices, totalNonSubscribableProductsPrice: extrasTotal }
				);

				if (nextDiscountData.onetime.isApplicable) {
					nextPrices.discountAmountOnetime = nextDiscountData.onetime.amount;
					nextPrices.freeNonSubscribableProducts =
						nextDiscountData.onetime.freeNonSubscribableProducts;
				}
			}

			const snapshot: ConfiguratorPricingSnapshot = {
				prices: nextPrices,
				discountData: nextDiscountData
			};

			configuratorPricesCache.set(cacheKey, clonePricingSnapshot(snapshot));

			return {
				cacheKey,
				...snapshot
			};
		})().finally(() => {
			configuratorPricesInFlightCache.delete(cacheKey);
			isFetchingPrices = false;
		});

		configuratorPricesInFlightCache.set(cacheKey, request);
		return request;
	}

	async function updatePrices() {
		if (!selectedCategory) {
			return;
		}

		const cacheKey = getPricingCacheKey(selectedCategory.id, selectedQuantity);
		latestPricesCacheKey = cacheKey;

		const result = await fetchPricingForOrder(order, selectedCategory.id, selectedQuantity);

		if (latestPricesCacheKey !== result.cacheKey) {
			return;
		}

		prices = result.prices;
		committedPricesCacheKey = result.cacheKey;
		discountData.onetime = result.discountData.onetime;
		discountData.recurring = result.discountData.recurring;
	}

	async function updateFromPrices() {
		if (!selectedCategory || fromPriceQuantity === null) {
			latestFromPricesCacheKey = null;
			resetFromPrices();
			fromDiscountData.onetime = null;
			fromDiscountData.recurring = null;
			return;
		}

		const pricingOrder = createPricingOrder(fromPriceQuantity);

		if (!pricingOrder) {
			latestFromPricesCacheKey = null;
			resetFromPrices();
			fromDiscountData.onetime = null;
			fromDiscountData.recurring = null;
			return;
		}

		const cacheKey = getPricingCacheKey(selectedCategory.id, fromPriceQuantity);
		latestFromPricesCacheKey = cacheKey;

		const result = await fetchPricingForOrder(pricingOrder, selectedCategory.id, fromPriceQuantity);

		if (latestFromPricesCacheKey !== result.cacheKey) {
			return;
		}

		fromPrices = result.prices;
		committedFromPricesCacheKey = result.cacheKey;
		fromDiscountData.onetime = result.discountData.onetime;
		fromDiscountData.recurring = result.discountData.recurring;
	}

	$effect(() => {
		if (isPricingActivated) {
			refreshPrices();
		}
	});

	async function refreshPrices() {
		if (!isPricingActivated) {
			return;
		}
		if (usesTotalPrice) return refreshTotalPrices();

		if (!isValidQuantity) {
			latestPricesCacheKey = null;
			resetPrices();
			discountData.onetime = null;
			discountData.recurring = null;
			await updateFromPrices();
			return;
		}

		latestFromPricesCacheKey = null;
		resetFromPrices();
		fromDiscountData.onetime = null;
		fromDiscountData.recurring = null;
		await updatePrices();
	}

	return {
		usesTotalPrice,
		quote: currentQuote,
		displayPrices,
		pricingFailed() {
			return usesTotalPrice && quoteError;
		},
		canSubmitPricing() {
			return !usesTotalPrice || currentQuote()?.payable != null;
		},
		isValid() {
			// A cart can consist solely of non-subscribable products, which have no category.
			if (!selectedCategory) {
				return selectedNonSubscribableProductsQuantity > 0;
			}

			return isValidCategory && isValidQuantity && isValidFrequency;
		},
		isValidCategory() {
			return isValidCategory;
		},
		isValidQuantity() {
			// @TODO: Remove quantity exception when backend implements a proper way to disallow specific quantities.
			return isValidQuantity || isProefpakket;
		},
		isValidFrequency() {
			return isValidFrequency;
		},
		isFetchingPrices() {
			return isFetchingPrices;
		},
		/**
		 * Whether `prices()` currently reflects the active category/quantity/frequency/coupon
		 * selection, as opposed to a stale snapshot left over from a previous selection while a
		 * newer request is still in flight.
		 */
		isPricesCurrent() {
			if (usesTotalPrice) return currentQuote(selectedFrequency, false) !== null;
			if (!selectedCategory) {
				return false;
			}

			return committedPricesCacheKey === getPricingCacheKey(selectedCategory.id, selectedQuantity);
		},
		/**
		 * Whether `fromPrices()` currently reflects the active "from price" quantity, as opposed
		 * to a stale snapshot left over from a previous quantity while a newer request is still
		 * in flight.
		 */
		isFromPricesCurrent() {
			if (usesTotalPrice) return currentQuote(selectedFrequency, true) !== null;
			if (!selectedCategory || fromPriceQuantity === null) {
				return false;
			}

			return (
				committedFromPricesCacheKey === getPricingCacheKey(selectedCategory.id, fromPriceQuantity)
			);
		},
		isValidCoupon() {
			return isValidCoupon;
		},
		isPricingActivated() {
			return isPricingActivated;
		},
		async activatePricing() {
			if (isPricingActivated) {
				return;
			}

			isPricingActivated = true;
			await refreshPrices();
		},
		isOneTimeOrderAllowed() {
			return frequencies.includes(0);
		},
		/**
		 * Checks whether the configurator is blocked from (frequency) changes
		 */
		isBlocked() {
			if (selectedFrequency <= 0) {
				return false;
			}

			return (
				discountData.recurring !== null &&
				discountData.recurring.isApplicable &&
				(discountData.recurring.blockingPeriod !== null ||
					discountData.recurring.requiredOrderCount !== null)
			);
		},
		blockingPeriod() {
			if (selectedFrequency === 0) {
				return discountData.onetime?.blockingPeriod ?? null;
			}

			return discountData.recurring?.blockingPeriod ?? null;
		},
		requiredOrderCount() {
			if (selectedFrequency === 0) {
				return discountData.onetime?.requiredOrderCount ?? null;
			}

			return discountData.recurring?.requiredOrderCount ?? null;
		},
		// @TODO: Remove quantity exception when backend implements a proper way to disallow specific quantities.
		isProefpakket() {
			return isProefpakket;
		},
		order() {
			return order;
		},
		categories() {
			return configuration.categories
				.map((category) => category.productCategory)
				.sort((a, b) => {
					if (a.id === configuration?.defaultCategory?.id) {
						return -1;
					}

					if (b.id === configuration?.defaultCategory?.id) {
						return 1;
					}

					return 0;
				});
		},
		products() {
			return products;
		},
		/**
		 * Groups categories by icon so multiple categories representing the same
		 * "type" (e.g. weight variants of coffee beans) can share a single toggle.
		 */
		categoryGroups() {
			const grouped = new Map<ProductCategoryIcon, ProductCategory[]>();

			for (const category of this.categories()) {
				if (!grouped.has(category.icon)) {
					grouped.set(category.icon, []);
				}

				grouped.get(category.icon)!.push(category);
			}

			return Array.from(grouped.entries()).map(([icon, groupCategories]) => ({
				icon,
				label: groupCategories[0].name,
				categories: [...groupCategories].sort((a, b) => a.rawProductWeight - b.rawProductWeight)
			}));
		},
		/**
		 * Selects a category group, defaulting to its lowest-weight category.
		 */
		selectGroup(icon: ProductCategoryIcon) {
			const group = this.categoryGroups().find((g) => g.icon === icon);

			if (group) {
				this.setCategory(group.categories[0]);
			}
		},
		/**
		 * Gets the total products price from API
		 *
		 * @returns number
		 */
		productsTotal() {
			return productsTotal;
		},
		/**
		 * Calculates the configuration's base total
		 *
		 * Does not call API's but uses the recurring products' base price to
		 * calculate a "from" price. Whenever possible, use productsTotal()
		 *
		 * @returns number
		 */
		productsBaseTotal() {
			return productsBaseTotal;
		},
		nonSubscribableProducts() {
			return nonSubscribableProducts;
		},
		nonSubscribableProductsTotal() {
			// @TODO: Reconsider this method if/when refactoring prices
			return nonSubscribableProductsTotal;
		},
		/**
		 * Gets the calculated combined total.
		 *
		 * @returns number
		 */
		total() {
			if (usesTotalPrice) {
				const quote = currentQuote();
				return quote?.payable ? moneyForDisplay(quote.beforeDiscount) : null;
			}
			return productsTotal + nonSubscribableProductsTotal;
		},
		/**
		 * Gets the base total for all products of the configuration.
		 *
		 * Does not call API's and should only be used as a "from" price.
		 *
		 * @returns number
		 */
		baseTotal() {
			return productsBaseTotal + nonSubscribableProductsTotal;
		},
		/**
		 * Set a product's quantity.
		 *
		 * @param product
		 * @param value Non-multiplied new quantity
		 * @returns void
		 */
		setProductQuantity(product: Product | NonSubscribableProduct, value: number) {
			const productItem = findProductItem(product);
			const multipliedValue = value * quantityMultiplier;
			const remainder = multipliedValue % quantityMultiplier;

			if (!productItem) {
				return;
			}

			// Make sure the new quantity is a valid value.
			if (remainder !== 0) {
				value = (multipliedValue - remainder) / quantityMultiplier;
			}

			productItem.quantity = value;
		},
		/**
		 * Increments product's quantity by 1, unless maximum quantity is reached.
		 *
		 * @param product
		 * @returns
		 */
		incrementProductQuantity(product: Product | NonSubscribableProduct) {
			const productItem = findProductItem(product);

			if (!productItem) {
				return;
			}

			productItem.quantity =
				isRegularProduct(productItem.product) && maximumQuantity > 0
					? Math.min(productItem.quantity + 1, maximumQuantity)
					: productItem.quantity + 1;
		},
		/**
		 * Decrements product's quantity by 1 but not lower than 0.
		 *
		 * @param product
		 * @returns
		 */
		decrementProductQuantity(product: Product | NonSubscribableProduct) {
			const productItem = findProductItem(product);

			if (!productItem) {
				return;
			}

			productItem.quantity = Math.max(productItem.quantity - 1, 0);
		},
		quantities() {
			return quantities;
		},
		frequencies() {
			return frequencies;
		},
		quantityMultiplier() {
			return quantityMultiplier;
		},
		prices() {
			return prices;
		},
		fromPrices() {
			return fromPrices;
		},
		hasFromPrices() {
			if (usesTotalPrice) return currentQuote(selectedFrequency, true)?.payable != null;
			return (
				fromPrices.productsLowestRecurringPrice !== null ||
				fromPrices.productsRecurringPrice !== null ||
				fromPrices.productsOneTimePrice !== null
			);
		},
		fromPriceQuantity() {
			return fromPriceQuantity;
		},
		discount() {
			if (usesTotalPrice) {
				const quote = currentQuote();
				return quote?.status === 'applied' ? moneyForDisplay(quote.discount) : 0;
			}
			if (selectedFrequency === 0) {
				return prices?.discountAmountOnetime ?? 0;
			}
			return prices?.discountAmount ?? 0;
		},
		discountData() {
			if (usesTotalPrice)
				return {
					recurring: quoteDiscount(currentQuote(selectedRecurringFrequency)),
					onetime: quoteDiscount(currentQuote(0))
				};
			return discountData;
		},
		couponErrorMessage() {
			return couponErrorMessage;
		},
		selectedCategory() {
			return selectedCategory;
		},
		selectedQuantity() {
			return selectedQuantity;
		},
		selectedNonSubscribableProductsQuantity() {
			return selectedNonSubscribableProductsQuantity;
		},
		selectedFrequency() {
			return selectedFrequency;
		},
		selectedRecurringFrequency() {
			return selectedRecurringFrequency;
		},
		setCategory(category: ProductCategory) {
			// Set the selected configuration category
			configurationCategory = configuration.categories.find(
				(c: OrderConfiguratorConfigurationCategory) => c.productCategory.id === category.id
			);
			resetAllPricing();
		},
		/**
		 * Sets the selected frequency
		 *
		 * Can be any frequency, including 0.
		 * @param newFrequency
		 */
		setFrequency(newFrequency: number) {
			selectedFrequency = newFrequency;

			if (newFrequency > 0) {
				selectedRecurringFrequency = newFrequency;
			}
		},
		/**
		 * Sets the preferred recurring frequency
		 *
		 * Has to be greater than 0.
		 * @param newFrequency
		 */
		setRecurringFrequency(newFrequency: number) {
			selectedRecurringFrequency = newFrequency;
		},
		setRecurringCouponCode(newCode: string) {
			setCouponCodes({
				recurringCode: newCode,
				onetimeCode: onetimeCouponCode
			});
		},
		setOnetimeCouponCode(newCode: string) {
			setCouponCodes({
				recurringCode: recurringCouponCode,
				onetimeCode: newCode
			});
		},
		setCouponCodes({ recurringCode, onetimeCode }: { recurringCode: string; onetimeCode: string }) {
			setCouponCodes({
				recurringCode,
				onetimeCode
			});
		},
		refreshPrices() {
			return refreshPrices();
		},
		couponValidationState() {
			return couponValidationState;
		},
		getPricingSnapshot(): ConfiguratorPricingSnapshot {
			return {
				pricingSource: usesTotalPrice ? 'total-price' : 'order-price',
				prices: $state.snapshot(prices),
				discountData: $state.snapshot(discountData)
			};
		},
		restorePricingSnapshot(snapshot: ConfiguratorPricingSnapshot) {
			// Quotes must be refreshed against their current basket/session, never restored as legacy prices.
			if (usesTotalPrice || snapshot.pricingSource === 'total-price') return;
			prices = snapshot.prices;
			discountData = snapshot.discountData;
			committedPricesCacheKey = selectedCategory
				? getPricingCacheKey(selectedCategory.id, selectedQuantity)
				: null;
		},
		get options() {
			return parsedOptions;
		}
	};
}

/**
 * Gets the calculated price for one shop item using the active pricing order.
 * Falls back to the configurator's valid "from" order when the selected quantity is invalid.
 */
export function getConfiguratorShopItemPrice(
	state: ConfiguratorState,
	vat: VatPresentation = 'included'
): number | null {
	if (state.usesTotalPrice) {
		const quote = state.quote();
		const quantity =
			state.products().length > 0
				? (state.fromPriceQuantity() ?? state.selectedQuantity())
				: state.selectedNonSubscribableProductsQuantity();
		return quote?.payable && quantity > 0 ? moneyForDisplay(quote.payable, vat) / quantity : null;
	}
	if (state.products().length === 0) {
		const [firstItem] = state.nonSubscribableProducts();
		return firstItem ? firstItem.product.price : null;
	}

	const fromQuantity = state.fromPriceQuantity();
	const quantity = fromQuantity ?? state.selectedQuantity();
	const prices = fromQuantity === null ? state.prices() : state.fromPrices();
	const isRecurring = state.selectedFrequency() > 0;
	const total = isRecurring ? prices.productsRecurringPrice : prices.productsOneTimePrice;
	const discount = isRecurring ? prices.discountAmount : prices.discountAmountOnetime;

	if (total === null || quantity <= 0) {
		return null;
	}

	return (total - discount) / quantity;
}

export function setConfiguratorState(configuratorState: ConfiguratorState) {
	setContext(key, configuratorState);
}

export function getConfiguratorState(): ConfiguratorState {
	return getContext(key);
}
